import { Action, Module, Mutation } from "vuex-class-modules"

import { apiService } from "@/services/api.service"
import { Endpoint } from "@/services/endpoints"

import { store } from "../store"
import { Modules } from "../modules"

import { BaseModule } from "@/models/BaseModule"
import { AlertContentItem, AlertType } from "@/models/Alert"
import { ServerPriorityResponse } from "@/models/Backlog"
import { ServerSendIdeaActivityNotification } from "@/models/common"
import { StructuredDescription } from "@/models/EditorData"
import { HttpError } from "@/models/Error"
import {
  applyIdeaActivitySse,
  Idea,
  IdeaStatus,
  IdeaStatusChange,
  IdeaVoteType
} from "@/models/Idea"
import { ServerResponse } from "@/models/ServerResponse"
import { SubscriptionDTO } from "@/models/Subscription"
import { FilterIdea } from "@/models"

import { AlertActions, alertModule } from "./alert.module"
import { projectModule } from "./project.module"
import { userModule } from "./user.module"

export interface IdeaCounts {
  totalIdeas: number
  openIdeas: number
  convertedIdeas: number
  obsoleteIdeas: number
  completedIdeas: number
}

/**
 * The same Idea can be cached in the list and as the open Idea;
 * the newest copy decides whether an update is stale.
 */
function findLatestIdea(lists: Idea[][], ideaId: string): Idea | undefined {
  let latest: Idea | undefined
  for (const list of lists) {
    const idea = list.find(item => item.id === ideaId)
    if (idea && (!latest || idea.modifiedDate.getTime() > latest.modifiedDate.getTime())) latest = idea
  }
  return latest
}

export enum IdeaActions {
  GET_IDEAS = "GET_IDEAS",
  GET_LATEST_CACHED_IDEA = "GET_LATEST_CACHED_IDEA",
  GET_IDEA_COUNTS = "GET_IDEA_COUNTS",
  GET_IDEA = "GET_IDEA",
  RESET_IDEA = "RESET_IDEA",
  SUBSCRIPTION_IDEA_CHANGE = "SUBSCRIPTION_IDEA_CHANGE",
  RESOLVE_IDEA_ID = "RESOLVE_IDEA_ID",
  CREATE_IDEA = "CREATE_IDEA",
  UPDATE_IDEA = "UPDATE_IDEA",
  VOTE_IDEA = "VOTE_IDEA",
  FOLLOW_IDEA = "FOLLOW_IDEA",
  SET_IDEA_READY = "SET_IDEA_READY",
  CHANGE_IDEA_PRIORITY = "CHANGE_IDEA_PRIORITY",
  CHANGE_IDEA_STATUS = "CHANGE_IDEA_STATUS",
  DELETE_IDEA = "DELETE_IDEA",
  CONVERT_IDEA_TO_EPIC = "CONVERT_IDEA_TO_EPIC",
  CONVERT_IDEA_TO_FEATURE = "CONVERT_IDEA_TO_FEATURE",
  CONVERT_IDEA_TO_ITEM = "CONVERT_IDEA_TO_ITEM"
}

@Module
class IdeaModule extends BaseModule {
  ideas: Idea[] = []
  ideasResponse: ServerResponse<Idea> = new ServerResponse<Idea>()
  idea: Idea = Idea.createDefault()
  counts: IdeaCounts = {
    totalIdeas: 0,
    openIdeas: 0,
    convertedIdeas: 0,
    obsoleteIdeas: 0,
    completedIdeas: 0
  }

  get projectId() {
    return projectModule.currentProjectId
  }

  get [`get/${IdeaActions.GET_IDEAS}`]() {
    return this.ideas
  }

  get [`get/${IdeaActions.GET_LATEST_CACHED_IDEA}`]() {
    return (ideaId: string) => findLatestIdea([this.ideas, [this.idea]], ideaId)
  }

  get [`get/${IdeaActions.GET_IDEA}`]() {
    return this.idea
  }

  get [`get/${IdeaActions.GET_IDEA_COUNTS}`]() {
    return this.counts
  }

  @Mutation
  setIdeas(serverResponse: ServerResponse<Idea>) {
    const content = serverResponse.content.map(idea => Idea.create(idea))
    this.ideas = content
    this.ideasResponse = { ...serverResponse, content }
  }

  @Mutation
  setIdea(idea: Idea) {
    this.idea = Idea.create(idea)
  }

  /** Clears the selected Idea when its query parameter changes. */
  @Action
  [IdeaActions.RESET_IDEA]() {
    this.setIdea(Idea.createDefault())
  }

  @Mutation
  setIdeaCounts(counts: IdeaCounts) {
    this.counts = counts
  }

  @Mutation
  setIdeaInList(idea: Idea) {
    const current = findLatestIdea([this.ideas, [this.idea]], idea.id)
    // The outgoing animation can finish after a newer update has already arrived through SSE.
    if (current && idea.modifiedDate.getTime() < current.modifiedDate.getTime()) return

    this.ideas = [...this.ideas.filter(item => item.id !== idea.id), idea].sort(
      (a, b) => a.priority - b.priority
    )
  }

  @Mutation
  removeIdea(ideaId: string) {
    this.ideas = this.ideas.filter(idea => idea.id !== ideaId)
    if (this.idea.id === ideaId) this.idea = Idea.createDefault()
  }

  /** Keeps the current user's follow state after the backend subscribes them on a mention. */
  @Mutation
  setIdeaFollowing(subscription: SubscriptionDTO) {
    if (subscription.resourceType !== "IDEA") return

    for (const idea of [this.idea, ...this.ideas]) {
      if (idea.id === subscription.resourceId) idea.following = subscription.enabled
    }
  }

  /**
   * Applies the project-wide preview through the same list update used for REST responses.
   * Counts are reread only when the Idea is new or its status changes.
   */
  @Action
  async [IdeaActions.SUBSCRIPTION_IDEA_CHANGE](activity: ServerSendIdeaActivityNotification) {
    if (activity.idea.projectId !== this.projectId) return

    const current = findLatestIdea([this.ideas, [this.idea]], activity.idea.id)
    const updated = applyIdeaActivitySse(current, activity, userModule.userProfile?.id)
    if (updated === current) return

    if (this.idea.id === updated.id) this.setIdea(updated)
    this.setIdeaInList(updated)

    // Not awaited: the list animates the row in or out as soon as the store changes.
    if (!current || current.status !== updated.status) {
      this[IdeaActions.GET_IDEA_COUNTS]()
    }
  }

  @Action
  async [IdeaActions.GET_IDEAS]({
    filterIdea,
    projectId
  }: {
    filterIdea: FilterIdea
    projectId?: string
  }) {
    const selectedProjectId = projectId || this.projectId
    const data = await apiService.get<ServerResponse<Idea>>(
      Endpoint.GET_IDEAS(selectedProjectId),
      filterIdea.getJsonObj()
    )
    return this.handleResponse<ServerResponse<Idea>>(data, data => {
      this.setIdeas(data)
      return this.ideasResponse
    })
  }

  @Action
  async [IdeaActions.GET_IDEA_COUNTS](projectId?: string) {
    const selectedProjectId = projectId || this.projectId
    const data = await apiService.get<IdeaCounts>(Endpoint.GET_IDEA_COUNTS(selectedProjectId))
    return this.handleResponse<IdeaCounts>(data, data => {
      this.setIdeaCounts(data)
      return data
    })
  }

  /** Reads the saved version after a reorder without replacing the open Idea when requested. */
  @Action
  async [IdeaActions.GET_IDEA]({
    ideaId,
    projectId,
    skipStoreUpdate,
    notFoundAsNull
  }: {
    ideaId: string
    projectId?: string
    skipStoreUpdate?: boolean
    notFoundAsNull?: boolean
  }) {
    const selectedProjectId = projectId || this.projectId
    const data = await apiService.get<Idea>(Endpoint.IDEA(selectedProjectId, ideaId))
    const isSilentNotFound = (error: HttpError) =>
      Boolean(notFoundAsNull) && error.response.status === 404
    return this.handleResponse<Idea>(
      data,
      data => {
        const idea = Idea.create(data)
        if (!skipStoreUpdate) this.setIdea(idea)
        return idea
      },
      isSilentNotFound,
      error => (isSilentNotFound(error) ? null : false)
    )
  }

  @Action
  async [IdeaActions.RESOLVE_IDEA_ID]({
    sequenceNumber,
    projectId
  }: {
    sequenceNumber: number
    projectId?: string
  }) {
    const selectedProjectId = projectId || this.projectId
    const data = await apiService.get<string>(Endpoint.RESOLVE_IDEA_ID(selectedProjectId, sequenceNumber))
    // The endpoint answers 204 with no body for a number this project has no idea for, and without
    // `responseRequired` handleResponse turns that empty body into `true` and it is used as an id.
    return this.handleResponse<string>(
      data,
      ideaId => ideaId,
      true,
      () => {
        throw Error(`Unknown idea number: ${sequenceNumber}`)
      },
      true
    )
  }

  @Action
  async [IdeaActions.CREATE_IDEA]({ idea, projectId }: { idea: Idea; projectId?: string }) {
    const selectedProjectId = projectId || this.projectId
    const data = await apiService.post<Idea>(
      Endpoint.GET_IDEAS(selectedProjectId),
      idea.getJsonObj()
    )
    return this.handleResponse<Idea>(data, data => {
      const created = Idea.create(data)
      this.setIdeaInList(created)
      const alertContent: AlertContentItem[] = [
        { text: "Idea", type: "bold" },
        { text: " successfully created", type: "regular" }
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent
      })
      return created
    })
  }

  @Action
  async [IdeaActions.UPDATE_IDEA]({ idea, projectId }: { idea: Idea; projectId?: string }) {
    const selectedProjectId = projectId || this.projectId
    const data = await apiService.patch<Idea>(
      Endpoint.IDEA(selectedProjectId, idea.id),
      idea.getJsonObj()
    )
    return this.handleResponse<Idea>(data, data => {
      const updated = Idea.create(data)
      this.setIdea(updated)
      this.setIdeaInList(updated)
      const alertContent: AlertContentItem[] = [
        { text: "Idea", type: "bold" },
        { text: " successfully updated", type: "regular" }
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent
      })
      return updated
    })
  }

  @Action
  async [IdeaActions.VOTE_IDEA]({
    ideaId,
    voteType,
    projectId
  }: {
    ideaId: string
    voteType: IdeaVoteType | null
    projectId?: string
  }) {
    const selectedProjectId = projectId || this.projectId
    const data = await apiService.post<Idea>(Endpoint.IDEA_VOTE(selectedProjectId, ideaId), {
      voteType
    })
    return this.handleResponse<Idea>(data, data => {
      const updated = Idea.create(data)
      this.setIdea(updated)
      this.setIdeaInList(updated)
      return updated
    })
  }

  @Action
  async [IdeaActions.FOLLOW_IDEA]({
    ideaId,
    enabled,
    projectId
  }: {
    ideaId: string
    enabled: boolean
    projectId?: string
  }) {
    const selectedProjectId = projectId || this.projectId
    const data = await apiService.post<Idea>(
      Endpoint.IDEA_FOLLOW(selectedProjectId, ideaId),
      {},
      { params: { enabled } }
    )
    return this.handleResponse<Idea>(data, data => {
      const updated = Idea.create(data)
      this.setIdea(updated)
      this.setIdeaInList(updated)
      const alertContent: AlertContentItem[] = updated.following
        ? [
            { text: "You're now following updates from ", type: "regular" },
            { text: `Idea ${updated.ideaKey}`, type: "bold" }
          ]
        : [
            { text: "You've unfollowed receiving updates from ", type: "regular" },
            { text: `Idea ${updated.ideaKey}`, type: "bold" }
          ]
      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent
      })
      return updated
    })
  }

  @Action
  async [IdeaActions.SET_IDEA_READY]({
    ideaId,
    ready,
    projectId
  }: {
    ideaId: string
    ready: boolean
    projectId?: string
  }) {
    const selectedProjectId = projectId || this.projectId
    const data = await apiService.patch<Idea>(
      Endpoint.IDEA_READY(selectedProjectId, ideaId),
      {},
      { params: { ready } }
    )
    return this.handleResponse<Idea>(data, data => {
      const updated = Idea.create(data)
      this.setIdea(updated)
      this.setIdeaInList(updated)
      const alertContent: AlertContentItem[] = [
        { text: updated.ideaKey, type: "bold" },
        { text: ` is marked as${updated.ready ? "" : " not"} Ready`, type: "regular" }
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent
      })
      return updated
    })
  }

  @Action
  async [IdeaActions.CHANGE_IDEA_PRIORITY]({
    ideaId,
    previousElement,
    nextElement,
    projectId
  }: {
    ideaId: string
    previousElement: Idea | null
    nextElement: Idea | null
    projectId?: string
  }) {
    const selectedProjectId = projectId || this.projectId
    const data = await apiService.patch<ServerPriorityResponse>(
      Endpoint.IDEA_CHANGE_PRIORITY(selectedProjectId, ideaId),
      {
        previousItemId: previousElement?.id || null,
        nextItemId: nextElement?.id || null
      }
    )
    return this.handleResponse<ServerPriorityResponse>(data, data => data)
  }

  @Action
  async [IdeaActions.CHANGE_IDEA_STATUS]({
    ideaId,
    status,
    message,
    fileMetadataIds,
    projectId
  }: {
    ideaId: string
    status: IdeaStatus
    message?: StructuredDescription
    fileMetadataIds?: string[]
    projectId?: string
  }) {
    const selectedProjectId = projectId || this.projectId
    const data = await apiService.post<IdeaStatusChange>(
      Endpoint.IDEA_CHANGE_STATUS(selectedProjectId, ideaId),
      { status, message, fileMetadataIds }
    )
    const changed: IdeaStatusChange | false = this.handleResponse<IdeaStatusChange>(
      data,
      data => data
    )
    if (!changed) return false

    const current = findLatestIdea([this.ideas, [this.idea]], ideaId)
    const saved: Idea | false = await this[IdeaActions.GET_IDEA]({
      ideaId,
      projectId: selectedProjectId,
      skipStoreUpdate: true
    })
    const updated = saved || Idea.create({ ...current, id: ideaId, status: changed.status })
    if (this.idea.id === ideaId) this.setIdea(updated)
    if (current) this.setIdeaInList(updated)
    this[IdeaActions.GET_IDEA_COUNTS]()
    return updated
  }

  @Action
  async [IdeaActions.DELETE_IDEA]({
    ideaId,
    projectId,
    skipStoreUpdate
  }: {
    ideaId: string
    projectId?: string
    skipStoreUpdate?: boolean
  }) {
    const selectedProjectId = projectId || this.projectId
    const ideaKey = this.getIdeaKey(ideaId)
    const data = await apiService.delete<void>(Endpoint.IDEA_DELETE(selectedProjectId, ideaId))
    return this.handleResponse<void>(data, () => {
      const alertContent: AlertContentItem[] = [
        { text: ideaKey ?? "Idea", type: "bold" },
        { text: " successfully deleted", type: "regular" }
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent
      })
      if (!skipStoreUpdate) this.removeIdea(ideaId)
      this[IdeaActions.GET_IDEA_COUNTS]()
      return true
    })
  }

  getIdeaKey(ideaId: string): string | undefined {
    if (this.idea.id === ideaId) return this.idea.ideaKey
    return this.ideas.find(idea => idea.id === ideaId)?.ideaKey
  }

  @Action
  async [IdeaActions.CONVERT_IDEA_TO_EPIC]({
    ideaId,
    projectId,
    skipStoreUpdate
  }: {
    ideaId: string
    projectId?: string
    skipStoreUpdate?: boolean
  }) {
    const selectedProjectId = projectId || this.projectId
    const data = await apiService.post<Idea>(
      Endpoint.IDEA_CONVERT_TO_EPIC(selectedProjectId, ideaId)
    )
    const ideaKey = this.getIdeaKey(ideaId)
    return this.handleResponse<Idea>(data, data => {
      const alertContent: AlertContentItem[] = ideaKey
        ? [
            { text: ideaKey, type: "bold" },
            { text: " converted to Epic", type: "regular" }
          ]
        : [{ text: "Idea converted to Epic", type: "regular" }]
      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent
      })
      const updated = Idea.create(data)
      if (!skipStoreUpdate) {
        this.setIdea(updated)
        this.setIdeaInList(updated)
      }
      this[IdeaActions.GET_IDEA_COUNTS]()
      return updated
    })
  }

  @Action
  async [IdeaActions.CONVERT_IDEA_TO_FEATURE]({
    ideaId,
    projectId,
    skipStoreUpdate
  }: {
    ideaId: string
    projectId?: string
    skipStoreUpdate?: boolean
  }) {
    const selectedProjectId = projectId || this.projectId
    const data = await apiService.post<Idea>(
      Endpoint.IDEA_CONVERT_TO_FEATURE(selectedProjectId, ideaId)
    )
    const ideaKey = this.getIdeaKey(ideaId)
    return this.handleResponse<Idea>(data, data => {
      const alertContent: AlertContentItem[] = ideaKey
        ? [
            { text: ideaKey, type: "bold" },
            { text: " promoted to Roadmap Feature", type: "regular" }
          ]
        : [{ text: "Idea promoted to Roadmap Feature", type: "regular" }]
      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent
      })
      const updated = Idea.create(data)
      if (!skipStoreUpdate) {
        this.setIdea(updated)
        this.setIdeaInList(updated)
      }
      this[IdeaActions.GET_IDEA_COUNTS]()
      return updated
    })
  }

  @Action
  async [IdeaActions.CONVERT_IDEA_TO_ITEM]({
    ideaId,
    projectId,
    skipStoreUpdate
  }: {
    ideaId: string
    projectId?: string
    skipStoreUpdate?: boolean
  }) {
    const selectedProjectId = projectId || this.projectId
    const data = await apiService.post<Idea>(
      Endpoint.IDEA_CONVERT_TO_ITEM(selectedProjectId, ideaId)
    )
    const ideaKey = this.getIdeaKey(ideaId)
    return this.handleResponse<Idea>(data, data => {
      const alertContent: AlertContentItem[] = ideaKey
        ? [
            { text: ideaKey, type: "bold" },
            { text: " converted to Backlog Item", type: "regular" }
          ]
        : [{ text: "Idea converted to Backlog Item", type: "regular" }]
      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent
      })
      const updated = Idea.create(data)
      if (!skipStoreUpdate) {
        this.setIdea(updated)
        this.setIdeaInList(updated)
      }
      this[IdeaActions.GET_IDEA_COUNTS]()
      return updated
    })
  }
}

export const ideaModule = new IdeaModule({ store, name: Modules.IDEA })
